import { createSearchParams } from 'react-router-dom'

import { Box, Button, List, Text } from '@factorialco/deprecated-design-system'
import { InvoicesDataTableDocument } from 'generated/resources/datatable.graphql'
import { BulkSendToPayInvoicesDocument } from 'generated/resources/graphql'
import { sum } from 'lodash'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FailedVendorData } from './types'

export const SendToPayModal = ({
  onClose,
  onSuccess,
  validInvoiceIds,
  failedVendors,
}: {
  onClose: () => void
  onSuccess: () => void
  validInvoiceIds: number[]
  failedVendors: FailedVendorData[]
}) => {
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const [bulkSendtoPayInvoices] = useMutation(BulkSendToPayInvoicesDocument, {
    extractorFn: (data) => data.financeMutations.bulkSendToPayFinanceFinancialDocument,
    refetchQueries: [InvoicesDataTableDocument],
  })

  const handleSendToPay = async (invoiceIds: number[]) => {
    const { success } = await asyncAction(async () => {
      return await bulkSendtoPayInvoices({
        variables: {
          ids: invoiceIds,
          companyId: company.id,
        },
      })
    })

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('finance.invoices.actions.send_to_pay_succeed', {
          count: validInvoiceIds.length,
        }),
      })
      onSuccess()
      return
    }
  }
  const vendorIdsParam = createSearchParams({
    ids: failedVendors.map((vendor) => vendor.vendorId.toString()),
  })

  const vendorsUrl = `${navigation.finance.spending.vendors.fullPath()}?${vendorIdsParam}`

  return (
    <Modal
      illustration="alert"
      title={i18n.tp('finance.invoices.send_to_pay_modal.title', {
        count: sum(failedVendors.map((v) => v.invoicesCounter)),
      })}
      description={
        validInvoiceIds.length == 0
          ? i18n.t('finance.invoices.send_to_pay_modal.description_no_valid_invoices')
          : i18n.tp('finance.invoices.send_to_pay_modal.description', {
              count: validInvoiceIds.length,
            })
      }
      onClose={onClose}
      onSubmit={() => handleSendToPay(validInvoiceIds)}
      footer={
        <>
          <Button.Router to={vendorsUrl} type="secondary">
            {i18n.tp('finance.invoices.send_to_pay_modal.go_to_vendors', {
              count: failedVendors.length,
            })}
          </Button.Router>
          <Button submit disabled={validInvoiceIds.length == 0}>
            {validInvoiceIds.length == 0
              ? i18n.t('finance.invoices.send_to_pay_modal.submit_no_results')
              : i18n.tp('finance.invoices.send_to_pay_modal.submit', {
                  count: validInvoiceIds.length,
                })}
          </Button>
        </>
      }
    >
      <Box>
        {failedVendors.length > 0 && (
          <List>
            {failedVendors.map((failedVendor) => (
              <List.Item key={failedVendor.vendorId}>
                <Text.Normal weight="medium" capitalize>
                  {failedVendor.vendorDescription}
                </Text.Normal>
                <Text.Normal>
                  {i18n.tp('finance.invoices.send_to_pay_modal.invoice_counter', {
                    count: failedVendor.invoicesCounter,
                  })}
                </Text.Normal>
              </List.Item>
            ))}
          </List>
        )}
      </Box>
    </Modal>
  )
}
